import { useMemo, useState, type FormEvent } from 'react';
import { formatMoney } from '@lombok/shared';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@lombok/ui';
import { FormActions, FormError, SelectField, TextField, TextareaField } from '@/components/form';
import { useAdjustStock, useProductCatalog, useSkus } from '@/data/hooks';
import { placeLabel } from '@/lib/names';
import { MOVEMENTS, toAdjustStockInput, type AdjustFieldErrors, type MovementChoice } from './adjustInput';

/** A balance the panel was opened for ("Ajustar" on a row); the SKU/location are then fixed. */
export interface StockAdjustTarget {
  skuId: string;
  locationId: string;
  /** Names shown instead of the raw ids when the list knows them. */
  productName?: string;
  skuLabel?: string;
}

interface StockAdjustPanelProps {
  /** Pre-selected balance (row action). Omit to receive stock for any SKU/location. */
  target?: StockAdjustTarget;
  /** Locations already in use, offered as suggestions (new ones can still be typed). */
  knownLocations: readonly string[];
  onCancel: () => void;
  /** Called with a short Spanish confirmation once the movement is saved. */
  onDone: (message: string) => void;
}

const TYPE_OPTIONS = (Object.keys(MOVEMENTS) as MovementChoice[]).map((value) => ({
  value,
  label: MOVEMENTS[value].label,
}));

/**
 * Ingresar / ajustar stock (admin, warehouse). The quantity is always a positive number and
 * the movement type says whether it adds or subtracts. Validation is the shared
 * `adjustStockSchema` — the same contract the API enforces, which stays authoritative (it also
 * refuses to go below the reserved units, and a concurrent edit returns 409).
 */
export function StockAdjustPanel({ target, knownLocations, onCancel, onDone }: StockAdjustPanelProps) {
  const catalog = useProductCatalog();
  const mutation = useAdjustStock();

  const [productId, setProductId] = useState('');
  const [skuId, setSkuId] = useState(target?.skuId ?? '');
  const [locationId, setLocationId] = useState(target?.locationId ?? '');
  const [choice, setChoice] = useState<MovementChoice>('receive');
  const [quantityText, setQuantityText] = useState('');
  const [reason, setReason] = useState('');
  const [reorderText, setReorderText] = useState('');
  const [errors, setErrors] = useState<AdjustFieldErrors>({});

  const skusQuery = useSkus(target ? undefined : productId || undefined);
  const productOptions = useMemo(
    () => (catalog.data?.items ?? []).filter((p) => p.active).map((p) => ({ value: p.productId, label: p.name })),
    [catalog.data],
  );
  const skuOptions = useMemo(
    () =>
      (skusQuery.data ?? [])
        .filter((s) => s.active)
        .map((s) => ({ value: s.skuId, label: `${s.code} · ${s.name} (${formatMoney(s.basePriceMinorUsd)})` })),
    [skusQuery.data],
  );

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    mutation.reset(); // drop a stale server error; this attempt is validated from scratch
    const result = toAdjustStockInput({ choice, skuId, locationId, quantityText, reason, reorderText });
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    mutation.mutate(result.input, {
      onSuccess: (saved) =>
        onDone(`Inventario actualizado: ${saved.availableQty} disponibles en ${placeLabel(saved.locationId)}.`),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{target ? 'Ajustar stock' : 'Ingresar stock'}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          {target ? (
            <p className="rounded-md bg-surface-muted p-3 text-sm text-text-primary">
              <span className="font-medium">{target.productName ?? target.skuId}</span>
              <span className="text-text-muted">
                {target.skuLabel ? ` · ${target.skuLabel}` : ''} · {placeLabel(target.locationId)}
              </span>
            </p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectField
                label="Producto"
                placeholder="Seleccionar producto…"
                options={productOptions}
                value={productId}
                onChange={(e) => {
                  setProductId(e.target.value);
                  setSkuId('');
                }}
              />
              <SelectField
                label="Presentación (SKU)"
                placeholder="Seleccionar presentación…"
                options={skuOptions}
                value={skuId}
                disabled={!productId || skuOptions.length === 0}
                error={errors.sku}
                hint={!productId ? 'Primero selecciona un producto.' : undefined}
                onChange={(e) => setSkuId(e.target.value)}
              />
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            {target ? null : (
              <>
                <TextField
                  label="Ubicación"
                  required
                  list="stock-locations"
                  placeholder="Ej. loc_caracas_main"
                  value={locationId}
                  error={errors.locationId}
                  hint="Elige una existente o escribe una nueva."
                  onChange={(e) => setLocationId(e.target.value)}
                />
                <datalist id="stock-locations">
                  {knownLocations.map((l) => (
                    <option key={l} value={l} />
                  ))}
                </datalist>
              </>
            )}
            <SelectField
              label="Tipo de movimiento"
              options={TYPE_OPTIONS}
              value={choice}
              onChange={(e) => setChoice(e.target.value as MovementChoice)}
            />
            <TextField
              label="Cantidad"
              required
              type="number"
              inputMode="numeric"
              min={1}
              step="1"
              value={quantityText}
              error={errors.quantity}
              hint={MOVEMENTS[choice].hint}
              onChange={(e) => setQuantityText(e.target.value)}
            />
            <TextField
              label="Punto de reorden (opcional)"
              type="number"
              inputMode="numeric"
              min={0}
              step="1"
              value={reorderText}
              error={errors.reorderPoint}
              hint="Avisa de bajo stock cuando lo disponible llega a este número."
              onChange={(e) => setReorderText(e.target.value)}
            />
          </div>

          <TextareaField
            label="Motivo"
            required
            placeholder="Ej. Recepción de la orden de compra 1042 / conteo físico."
            value={reason}
            error={errors.reason}
            onChange={(e) => setReason(e.target.value)}
          />

          <FormError error={mutation.error} />
          <FormActions>
            <Button type="button" variant="outline" onClick={onCancel} disabled={mutation.isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Guardando…' : 'Guardar movimiento'}
            </Button>
          </FormActions>
        </form>
      </CardContent>
    </Card>
  );
}
